import { useState, useRef, useEffect } from "react";
import { Bot, X, Send, Sparkles } from "lucide-react";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AiChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef("");
  const bottomRef = useRef(null);
  const { lang } = useLang();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", content: err.detail || "Ralat / Error" };
          return c;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(part.slice(6));
            if (payload.session_id) sessionRef.current = payload.session_id;
            if (payload.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + payload.delta };
                return c;
              });
            }
            if (payload.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: "Ralat AI: " + payload.error };
                return c;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Gagal berhubung / Connection failed" };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-trigger"
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-brand-deep text-gold shadow-xl flex items-center justify-center hover:bg-brand-dark hover:scale-105 transition-transform"
      >
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
      </button>
      {open && (
        <div
          data-testid="ai-chat-drawer"
          className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 h-[60vh] bg-card rounded-2xl border border-brand-deep/15 shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="bg-brand-dark px-4 py-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="font-display font-semibold text-white text-sm">
              {lang === "bm" ? "Pembantu AI Koperasi" : "Koperasi AI Assistant"}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground mt-8 px-4">
                {lang === "bm"
                  ? "Tanya saya tentang saham, yuran, penyata atau peraturan koperasi."
                  : "Ask me about shares, fees, statements or cooperative rules."}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-brand-deep text-white rounded-br-sm" : "bg-brand-light text-brand-dark rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-brand-deep/10 flex gap-2">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={lang === "bm" ? "Tulis soalan..." : "Type a question..."}
              className="flex-1 text-sm rounded-full border border-input px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            />
            <button
              data-testid="ai-chat-send-btn"
              onClick={send}
              disabled={streaming}
              className="w-10 h-10 rounded-full bg-gold text-brand-dark flex items-center justify-center hover:bg-gold-hover transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
